import {put, call, takeLatest, select} from 'redux-saga/effects';
import invoiceUploadApi from '../api/invoiceUpload.api';
import productApi from '../api/product.api';
import * as ActionTypes from '../actions/types.action';
import {getBearerHeader} from '../helpers/api.helper';
import {
  ERRORS,
  HTTP_STATUS,
  INVOICE_UPLOAD_ANONYMOUS_QUOTA_EXCEEDED_MESSAGE,
} from '../constants/constants';

// True when the axios error / response looks like an expired or missing
// auth token. The global response interceptor in helpers/api.helper.js has
// an edge case for staff users where a 401 is "resolved" as `{error: ...}`
// instead of rejecting, which then makes `.then(res => res.data)` return
// undefined. We treat both the rejection shape (`e.code === NEED_AUTH` or
// `e.response.status === 401`) and the silent-undefined shape as expired.
const isAuthExpiredError = e => {
  if (!e) return false;
  if (e.code === ERRORS.NEED_AUTH) return true;
  if (e.error === ERRORS.UNAUTHORIZED || e.error === ERRORS.EXPIRED)
    return true;
  if (e.response && e.response.status === 401) return true;
  return false;
};

// The global interceptor (helpers/api.helper.js) rejects a 500 with just the
// parsed body, stripping the {status, data} wrapper it otherwise leaves alone
// for every other status — normalise both shapes here so callers only ever
// deal with one.
const normalizeErrorResponse = e => {
  if (e && e.response) {
    return {status: e.response.status, data: e.response.data};
  }
  return {status: 500, data: e};
};

function* uploadInvoiceAuthenticated(action, headers) {
  const response = yield call(
    invoiceUploadApi.upload,
    action.files,
    action.fulfillmentType,
    headers
  );
  yield put({type: ActionTypes.UPLOAD_INVOICE_SUCCESS, response});
  yield put({type: ActionTypes.FETCH_UPLOADED_INVOICES_REQUESTED, page: 0});
}

// Anonymous upload errors carry a JSON body describing exactly what went
// wrong (see anonymous-price-comparison-api.pdf §1) — there is no bare
// 401/403 from this API, so every failure is handled here rather than via
// isAuthExpiredError.
function* uploadInvoiceAnonymous(action) {
  try {
    const response = yield call(
      invoiceUploadApi.uploadAnonymous,
      action.files,
      action.fulfillmentType
    );
    yield put({type: ActionTypes.UPLOAD_INVOICE_SUCCESS, response});
  } catch (e) {
    const {status, data} = normalizeErrorResponse(e);
    if (status === 429 && data && typeof data.limit === 'number') {
      // Same AnonymousUploadQuota shape as GET /quota — updating the shared
      // quota state swaps the form for the register CTA on this and future
      // visits. Also surface it as an upload error so the popup shown for
      // this attempt explains why it didn't go through.
      yield put({type: ActionTypes.FETCH_ANONYMOUS_QUOTA_SUCCESS, quota: data});
      yield put({
        type: ActionTypes.UPLOAD_INVOICE_FAILED,
        error: INVOICE_UPLOAD_ANONYMOUS_QUOTA_EXCEEDED_MESSAGE,
      });
      return;
    }
    if (status === 429) {
      // Azure Front Door's own rate limiting on this path returns its own
      // 403/429 page rather than a parseable AnonymousUploadQuota body.
      yield put({
        type: ActionTypes.UPLOAD_INVOICE_FAILED,
        error: 'Too many requests — please try again shortly.',
      });
      return;
    }
    if (status === 503 && !data) {
      yield put({type: ActionTypes.INVOICE_UPLOAD_FEATURE_UNAVAILABLE});
      return;
    }
    if (status === HTTP_STATUS.FORBBIDEN) {
      yield put({
        type: ActionTypes.UPLOAD_INVOICE_FAILED,
        error: 'Please log in to use price comparison',
      });
      return;
    }
    // Anything else without a parseable AnonymousUploadQuota body has no
    // user-meaningful detail to show — `e.message` here is axios's generic
    // "Request failed with status code NNN", which is not something we
    // should surface. The popup this feeds already shows "We couldn't
    // upload your invoice" as its title, so this is just the supporting
    // line underneath it.
    yield put({
      type: ActionTypes.UPLOAD_INVOICE_FAILED,
      error: (data && data.message) || 'Please try again later.',
    });
  }
}

function* uploadInvoice(action) {
  const getAuth = state => state.auth;
  const storeToken = yield select(getAuth);
  const headers = getBearerHeader(null, storeToken);
  if (!headers) {
    yield call(uploadInvoiceAnonymous, action);
    return;
  }
  try {
    yield call(uploadInvoiceAuthenticated, action, headers);
  } catch (e) {
    if (e && e.requestInterrupted) {
      // Cancelled (e.g. tab backgrounded) — not a real failure, just let the
      // user retry rather than forcing a re-login over it.
      yield put({
        type: ActionTypes.UPLOAD_INVOICE_FAILED,
        error: 'Upload was interrupted. Please try again.',
      });
      return;
    }
    if (isAuthExpiredError(e)) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    yield put({
      type: ActionTypes.UPLOAD_INVOICE_FAILED,
      error: e.message || 'Upload failed',
    });
  }
}

function* fetchUploadedInvoices(action) {
  try {
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(null, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    const data = yield call(
      invoiceUploadApi.list,
      action.page || 0,
      20,
      headers
    );
    yield put({type: ActionTypes.FETCH_UPLOADED_INVOICES_SUCCESS, data});
  } catch (e) {
    if (e && e.requestInterrupted) {
      // Cancelled (e.g. tab backgrounded) — not a real failure, just let the
      // user retry rather than forcing a re-login over it.
      yield put({
        type: ActionTypes.FETCH_UPLOADED_INVOICES_FAILED,
        error: 'Loading was interrupted. Please try again.',
      });
      return;
    }
    if (isAuthExpiredError(e)) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    yield put({
      type: ActionTypes.FETCH_UPLOADED_INVOICES_FAILED,
      error: e.message || 'Failed to load invoices',
    });
  }
}

function* fetchUploadedInvoiceDetail(action) {
  // Anonymous invoices are never linked to an account, so once we know this
  // id came from the anonymous flow we always poll the public endpoint —
  // including after the visitor logs in mid-comparison — rather than
  // re-deciding from live auth state each poll and losing the result to a
  // 404 on the authenticated endpoint.
  if (action.isAnonymous) {
    try {
      const data = yield call(invoiceUploadApi.getDetailAnonymous, action.id);
      yield put({
        type: ActionTypes.FETCH_UPLOADED_INVOICE_DETAIL_SUCCESS,
        data,
        silent: action.silent,
      });
    } catch (e) {
      if (e && e.requestInterrupted) {
        if (action.silent) return;
        yield put({
          type: ActionTypes.FETCH_UPLOADED_INVOICE_DETAIL_FAILED,
          error: 'Loading was interrupted. Please try again.',
          silent: false,
        });
        return;
      }
      const {status} = normalizeErrorResponse(e);
      yield put({
        type: ActionTypes.FETCH_UPLOADED_INVOICE_DETAIL_FAILED,
        // Deliberately 404 (not 403) so ids can't be probed.
        error:
          status === HTTP_STATUS.NOT_FOUND
            ? "We couldn't find that comparison."
            : 'Failed to load invoice detail. Please try again.',
        silent: action.silent,
      });
    }
    return;
  }

  try {
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(null, storeToken);
    if (!headers) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    const data = yield call(
      action.isReferral
        ? invoiceUploadApi.getReferralComparison
        : invoiceUploadApi.getDetail,
      action.id,
      headers
    );
    yield put({
      type: ActionTypes.FETCH_UPLOADED_INVOICE_DETAIL_SUCCESS,
      data,
      silent: action.silent,
    });
  } catch (e) {
    if (e && e.requestInterrupted) {
      // Cancelled (e.g. tab backgrounded mid-poll) — not a real failure. A
      // silent poll tick just drops here and the next tick retries on its
      // own; treating this as an auth failure used to force a re-login that
      // wiped the whole invoiceUpload state (including `detail`), making an
      // already-processed invoice look like it was starting over from
      // scratch when the user came back.
      if (action.silent) return;
      yield put({
        type: ActionTypes.FETCH_UPLOADED_INVOICE_DETAIL_FAILED,
        error: 'Loading was interrupted. Please try again.',
        silent: false,
      });
      return;
    }
    if (isAuthExpiredError(e)) {
      yield put({type: ActionTypes.ERROR_REQUIRE_LOGIN});
      return;
    }
    const status = e.response && e.response.status;
    let error;
    if (status === HTTP_STATUS.NOT_FOUND) {
      error = 'Invoice not found.';
    } else if (status === HTTP_STATUS.FORBBIDEN) {
      error = "You don't have access to this invoice.";
    } else {
      error = 'Failed to load invoice detail. Please try again.';
    }
    yield put({
      type: ActionTypes.FETCH_UPLOADED_INVOICE_DETAIL_FAILED,
      error,
      silent: action.silent,
    });
  }
}

function* fetchAnonymousQuota() {
  try {
    const quota = yield call(invoiceUploadApi.getAnonymousQuota);
    yield put({type: ActionTypes.FETCH_ANONYMOUS_QUOTA_SUCCESS, quota});
  } catch (e) {
    const {status, data} = normalizeErrorResponse(e);
    if (status === 503 && !data) {
      yield put({type: ActionTypes.INVOICE_UPLOAD_FEATURE_UNAVAILABLE});
      return;
    }
    yield put({
      type: ActionTypes.FETCH_ANONYMOUS_QUOTA_FAILED,
      error:
        (data && data.message) ||
        e.message ||
        'Failed to check your comparison allowance.',
    });
  }
}

// 401 here means the token has no C account (e.g. a staff login) rather than
// a real failure — nothing to show, so resolve to an empty list instead of
// surfacing an error on an otherwise-working results page.
function* fetchInvoiceFavouriteIds() {
  try {
    const getAuth = state => state.auth;
    const storeToken = yield select(getAuth);
    const headers = getBearerHeader(null, storeToken);
    if (!headers) {
      yield put({
        type: ActionTypes.FETCH_INVOICE_FAVOURITE_IDS_SUCCESS,
        favouriteIds: [],
      });
      return;
    }
    const favouriteIds = yield call(productApi.getFavouriteIds, headers);
    yield put({
      type: ActionTypes.FETCH_INVOICE_FAVOURITE_IDS_SUCCESS,
      favouriteIds: favouriteIds || [],
    });
  } catch (e) {
    if (e && e.response && e.response.status === HTTP_STATUS.UNAUTHORIZED) {
      yield put({
        type: ActionTypes.FETCH_INVOICE_FAVOURITE_IDS_SUCCESS,
        favouriteIds: [],
      });
      return;
    }
    yield put({
      type: ActionTypes.FETCH_INVOICE_FAVOURITE_IDS_FAILED,
      error: e.message,
    });
  }
}

export default function* invoiceUploadSaga() {
  yield takeLatest(ActionTypes.UPLOAD_INVOICE_REQUESTED, uploadInvoice);
  yield takeLatest(
    ActionTypes.FETCH_UPLOADED_INVOICES_REQUESTED,
    fetchUploadedInvoices
  );
  yield takeLatest(
    ActionTypes.FETCH_UPLOADED_INVOICE_DETAIL_REQUESTED,
    fetchUploadedInvoiceDetail
  );
  yield takeLatest(
    ActionTypes.FETCH_ANONYMOUS_QUOTA_REQUESTED,
    fetchAnonymousQuota
  );
  yield takeLatest(
    ActionTypes.FETCH_INVOICE_FAVOURITE_IDS_REQUESTED,
    fetchInvoiceFavouriteIds
  );
}
